import React, { lazy } from 'react';

import type { TaskStatus } from '@app/api/editor';
import { ReactWebComponent } from '@app/utils/web-components';

const AppProviders = lazy(() => import('@app/components/AppProviders'));
const TranslationActions = lazy(() => import('.'));

const emitSocketEvent = (event: string, data: Record<string, string | number | boolean | null>) => {
    // @ts-expect-error globalSocket is not defined
    window.globalSocket?.emit(event, data);
};

class TranslationActionsComponent extends ReactWebComponent {
    static get observedAttributes() {
        return [
            'projectId',
            'langId',
            'keyId',
            'taskId',
            'taskItemId',
            'referenceLanguageId',
            'translationId',
            'taskStatus',
        ];
    }

    render() {
        const handleTranslationUpdated = (translationId: number, projectId: string, branchId: string | null) => {
            emitSocketEvent('translation updated', {
                translationId,
                projectId,
                branchId,
            });
        };

        const handleTaskClosed = (taskId: number, projectId: string) => {
            emitSocketEvent('task closed', {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                task_id: taskId,
                projectId,
            });
            document.dispatchEvent(new CustomEvent('task_closed', { detail: { taskId } }));
        };

        const handleTaskLanguageCompleted = (taskId: number, languageId: number, projectId: string) => {
            emitSocketEvent('task lang complete', {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                task_id: taskId,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                lang_id: languageId,
                projectId,
            });

            document.dispatchEvent(
                new CustomEvent('task_lang_complete', {
                    detail: {
                        taskId,
                        languageId,
                    },
                }),
            );
        };

        const handleTaskItemCompleted = (taskId: number, taskItemId: number, completed: boolean, projectId: string) => {
            emitSocketEvent('task item complete', {
                // eslint-disable-next-line @typescript-eslint/naming-convention
                task_id: taskId,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                task_item_id: taskItemId,
                complete: completed,
                projectId,
            });

            document.dispatchEvent(
                new CustomEvent('task_item_complete', {
                    detail: {
                        taskId,
                        taskItemId,
                        completed,
                    },
                }),
            );
        };

        const projectId = this.getAttribute('projectId');
        const langId = this.jsonProp<number>('langId');
        const keyId = this.jsonProp<number>('keyId');
        const taskId = this.jsonProp<number>('taskId');
        const taskItemId = this.jsonProp<number>('taskItemId');
        const referenceLanguageId = this.jsonProp<number>('referenceLanguageId');
        const translationId = this.jsonProp<number>('translationId');
        const taskStatus = this.getAttribute('taskStatus') as TaskStatus;

        if (!projectId || langId === null || keyId === null || translationId === null) {
            return <>Error</>;
        }

        return (
            <AppProviders>
                <TranslationActions
                    projectId={projectId}
                    languageId={langId}
                    keyId={keyId}
                    taskId={taskId || 0}
                    taskItemId={taskItemId || 0}
                    referenceLanguageId={referenceLanguageId || 0}
                    translationId={translationId}
                    taskStatus={taskStatus}
                    onTranslationUpdated={handleTranslationUpdated}
                    onTaskClosed={handleTaskClosed}
                    onTaskLanguageCompleted={handleTaskLanguageCompleted}
                    onTaskItemCompleted={handleTaskItemCompleted}
                />
            </AppProviders>
        );
    }
}

customElements.define('editor-actions', TranslationActionsComponent);
